export const MIN_CANVAS_ZOOM = 0.35;
export const MAX_CANVAS_ZOOM = 2;

export type CanvasRect = {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
};

export type CanvasViewport = {
  zoom: number;
  scrollLeft: number;
  scrollTop: number;
};

export const clampZoom = (zoom: number) =>
  Math.min(MAX_CANVAS_ZOOM, Math.max(MIN_CANVAS_ZOOM, Math.round(zoom * 100) / 100));

export const rectanglesOverlap = (
  left: CanvasRect,
  right: CanvasRect,
  horizontalGap = 0,
  verticalGap = 0
) => !(
  left.x + left.width + horizontalGap <= right.x ||
  right.x + right.width + horizontalGap <= left.x ||
  left.y + left.height + verticalGap <= right.y ||
  right.y + right.height + verticalGap <= left.y
);

export const resolveCollisions = (
  nodes: CanvasRect[],
  gaps = { horizontal: 32, vertical: 28 }
): CanvasRect[] => {
  const placed: CanvasRect[] = [];
  nodes.forEach((source) => {
    const node = { ...source, x: Math.max(16, source.x), y: Math.max(16, source.y) };
    let guard = 0;
    while (placed.some((candidate) => rectanglesOverlap(node, candidate, gaps.horizontal, gaps.vertical))) {
      const collisions = placed.filter((candidate) =>
        rectanglesOverlap(node, candidate, gaps.horizontal, gaps.vertical)
      );
      node.y = Math.max(...collisions.map((candidate) => candidate.y + candidate.height + gaps.vertical));
      guard += 1;
      if (guard > nodes.length + 1) {
        node.x += node.width + gaps.horizontal;
        node.y = 16;
        guard = 0;
      }
    }
    placed.push(node);
  });
  return placed;
};

export const fitViewport = (
  content: { width: number; height: number },
  viewport: { width: number; height: number },
  padding = 40
): CanvasViewport => {
  const usableWidth = Math.max(1, viewport.width - padding * 2);
  const usableHeight = Math.max(1, viewport.height - padding * 2);
  return {
    zoom: clampZoom(Math.min(usableWidth / Math.max(1, content.width), usableHeight / Math.max(1, content.height))),
    scrollLeft: 0,
    scrollTop: 0,
  };
};
